import AppLayout from '../../layouts/AppLayout';
import { useRecommendedCommunities } from '../../api-services/community/useRecommendedCommunities';
import { useCommunities } from '../../api-services/community/use-communities';
import community_api from '../../api-services/community/community.services';
import { Community } from '../../app-schemas/community/community.interface';
import { Link, useNavigate } from 'react-router-dom';
import { SITELINK } from '../../app-routes/Links';
import { CommunityImage } from '../../assets/custom-icons';
import { tinyAlert, truncateText } from '../../app-utils';
import { useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import CommunityTopBar from './CommunityTopBar';
import { SkeletonCommunity, SkeletonCommunityPage } from '../../components/skeletons';
import ErrorState from '../../components/ErrorState';
import { useTheme } from '../../context/theme-context';

const Communities = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [joinLoadingId, setJoinLoadingId] = useState<number | null>(null);

  const {
    data: userCommunities,
    isLoading: isUserCommunitiesLoading,
    isSuccess: isUserCommunitiesSuccess,
    isError: isUserCommunitiesError,
    refetch: refetchUserCommunities,
    error: userCommunitiesError,
  } = useCommunities();

  const {
    data: recommendedCommunities,
    isLoading: isRecommendedLoading,
    isError: isRecommendedError,
    refetch: refetchRecommended,
    error: recommendedError,
  } = useRecommendedCommunities({ enabled: isUserCommunitiesSuccess });

  const handleJoinCommunity = async (id: number) => {
    try {
      setJoinLoadingId(id);
      const res = await community_api.JoinCommunity(id);
      if (res?.data?.status) {
        tinyAlert('success', 'Successfully joined community');
        await refetchRecommended();
        await refetchUserCommunities();
      }
    } catch (error) {
      tinyAlert('error', 'An error occurred! Please try again.');
      console.error('Error joining community:', error);
    } finally {
      setJoinLoadingId(null);
    }
  };

  const handleCreateCommunity = () => {
    navigate(SITELINK.NEW_COMMUNITIES);
  };

  const handleRetry = () => {
    refetchUserCommunities();
    refetchRecommended();
  };

  // Show loading skeleton for the entire page
  if (isUserCommunitiesLoading) {
    return (
      <AppLayout title="Catapot - Community">
        <SkeletonCommunityPage />
      </AppLayout>
    );
  }

  // Show error state for user communities
  if (isUserCommunitiesError) {
    return (
      <AppLayout title="Catapot - Community">
        <CommunityTopBar />
        <ErrorState
          title="Failed to load your communities"
          message={userCommunitiesError?.message || "Unable to fetch your communities. Please check your connection and try again."}
          onRetry={handleRetry}
          showRetry={true}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Catapot - Community">
      <CommunityTopBar />
      
      {/* Header Section */}
      <div className="w-full bg-white dark:bg-neutral-800 flex items-center justify-between py-3 px-4 border-b dark:border-gray-700">
        <h2 className="font-[400] text-center text-[20px] madimi-one text-black dark:text-white">
          Communities
        </h2>
        <button
          type="button"
          onClick={handleCreateCommunity}
          className="bg-[#1976D2] text-white font-semibold rounded-full p-2 text-[13px] hover:bg-[#1565C0] transition-colors"
          aria-label="Create community"
        >
          <FaPlus />
        </button>
      </div>

      <section>
        <div className="flex flex-col w-full pb-2 mb-12 dark:bg-neutral-800">
          {/* User communities section */}
          <div className="mb-4">
            {isUserCommunitiesLoading ? (
              <SkeletonCommunity variant="user" count={3} />
            ) : (
              <>
                {userCommunities?.data?.length > 0 ? (
                  userCommunities?.data?.map((community: Community, index: number) => (
                    <Link
                      key={community.id || index}
                      to={`${SITELINK.COMMUNITY_ACTIVITIES}/${community.id}`}
                      className="border-b bg-white dark:bg-neutral-800 px-5 gap-1 p-[1px] justify-between border-[#CED5DC] dark:border-gray-700 items-center flex hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
                    >
                      <div className="flex-grow flex py-4">
                        <div className="overflow-hidden flex justify-center">
                          <img
                            src={community.cover_image_url || CommunityImage}
                            alt={community.name}
                            className="object-cover rounded-[50px] w-[60px] h-[50px] overflow-hidden"
                            loading="lazy"
                          />
                        </div>
                        <div className="px-2 w-full">
                          <p className="overflow-hidden text-[10px] flex-col text-base font-medium m-0 flex gap-1 madimi-one text-black dark:text-white font-[400]">
                            {truncateText(community.name, 25)}
                          </p>
                          <div className="flex-col">
                            <p className="text-gray-700 dark:text-gray-300 font-[500] text-xs">
                              {`${community.members} member${community.members !== 1 ? 's' : ''}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-8 px-4">
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      You haven't joined any communities yet
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Join communities below to get started!
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Recommended communities section */}
          {isUserCommunitiesSuccess && (
            <div className="mt-4">
              <div className="px-5 mb-2">
                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Recommended for you
                </h3>
              </div>
              
              {isRecommendedLoading ? (
                <SkeletonCommunity variant="recommended" count={3} />
              ) : isRecommendedError ? (
                <ErrorState
                  title="Failed to load recommendations"
                  message={recommendedError?.message || "Unable to fetch recommended communities."}
                  onRetry={() => refetchRecommended()}
                  showRetry={true}
                />
              ) : (
                <>
                  {recommendedCommunities?.length > 0 ? (
                    recommendedCommunities?.map((community: Community, index: number) => (
                      <div
                        key={community.id || index}
                        className="border-b bg-white dark:bg-neutral-800 px-5 gap-1 p-[1px] justify-between border-[#CED5DC] dark:border-gray-700 items-center flex bg-[#1976d21c] dark:bg-neutral-800/50"
                      >
                        <div className="flex-grow flex py-4">
                          <div className="overflow-hidden flex justify-center">
                            <img
                              src={community.cover_image_url || CommunityImage}
                              alt={community.name}
                              className="object-cover rounded-[50px] w-[60px] h-[50px] shadow-sm overflow-hidden"
                              loading="lazy"
                            />
                          </div>
                          <div className="px-2 w-full">
                            <p className="overflow-hidden text-[10px] flex-col text-base font-medium m-0 flex gap-1 madimi-one text-black dark:text-white font-[400]">
                              {truncateText(community.name, 20)}
                            </p>
                            <div className="flex-col">
                              <p className="text-gray-700 dark:text-gray-300 font-[500] text-xs">
                                {`${community.members} member${community.members !== 1 ? 's' : ''}`}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="w-auto flex justify-center px-2">
                          <button
                            onClick={() => handleJoinCommunity(community.id)}
                            disabled={joinLoadingId === community.id}
                            className="justify-center px-5 py-2 rounded-full w-auto h-auto shadow-sm flex font-semibold text-white bg-[#1976D2] hover:bg-[#1565C0] disabled:bg-[#1976D2]/50 disabled:cursor-not-allowed text-xs gap-1 transition-colors"
                          >
                            {joinLoadingId === community.id ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <span className="capitalize text-xs">JOIN</span>
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 px-4">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No recommended communities available
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Check back later for personalized recommendations
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </AppLayout>
  );
};

export default Communities;